import { create } from 'zustand';
import type {
  DraftContact,
  DraftJointHolder,
} from '../hooks/useSoteriaChat';

/**
 * pendingAccountDraftStore
 * ------------------------
 * In-memory hand-off from the Client Assistant's "account_draft" card into the
 * account-opening wizard. Mirrors pendingPromptStore.
 *
 * Flow:
 *   1. Advisor clicks "Review & open" on the AccountDraftCard →
 *      `setPending({ clientId, clientName, custodian, counts })` →
 *      `navigate({ to: '/clients/$clientId/open-account' })`.
 *   2. OpenAccountPage mounts → reads `pending` → jumps straight into the
 *      in-person wizard for that client with step-1 (custodian + account
 *      counts) pre-filled → `clearPending()`.
 *
 * NOT persisted: a refresh should NOT replay an old draft. The agent only ever
 * proposes the account SELECTION here — the wizard still gathers/validates all
 * KYC + compliance and the advisor signs.
 */
export interface PendingAccountDraft {
  clientId: string;
  clientName: string;
  custodian: 'IBKR' | 'NBIN';
  counts: Record<string, number>; // accountType code → quantity
  // Wizard-shaped "More Details" demographics (gender / marital status).
  details?: { gender?: string; maritalStatus?: string };
  // Optional extras the assistant gathered: a joint holder (becomes the wizard's
  // second holder) and any trusted/additional contacts. The primary holder's
  // KYC still hydrates from the client record, not from here.
  holders?: Array<DraftJointHolder>;
  contacts?: Array<DraftContact>;
}

interface PendingAccountDraftStore {
  pending: PendingAccountDraft | null;
  setPending: (draft: PendingAccountDraft) => void;
  clearPending: () => void;
}

export const usePendingAccountDraftStore = create<PendingAccountDraftStore>(
  (set) => ({
    pending: null,
    setPending: (draft) => set({ pending: draft }),
    clearPending: () => set({ pending: null }),
  }),
);
